/**
 * Active-filter chips for the Scheduled Lines toolbar.
 *
 * One chip per filter that currently narrows the list — including the
 * defaults a schedule-line tenant opens with — so nothing is ever filtered
 * silently. The × on a chip clears just that filter; "Clear all" restores
 * the unfiltered list. Sits between the toolbar and the tab strip.
 */
import { X, Filter } from 'lucide-react';
import {
  type ScheduleFilters,
  SCHEDULE_FILTER_LABELS,
  describeFilterValue,
} from './types';

interface ActiveFilterChipsProps {
  filters: ScheduleFilters;
  /** Keys that were applied by default rather than chosen — chip reads "default". */
  defaultKeys?: ReadonlySet<keyof ScheduleFilters>;
  onRemove: (key: keyof ScheduleFilters) => void;
  onClearAll: () => void;
}

/** Filters that live in their own control on the toolbar, not in this strip. */
const TOOLBAR_OWNED: ReadonlySet<keyof ScheduleFilters> = new Set<keyof ScheduleFilters>(['partSearch']);

export function ActiveFilterChips({ filters, defaultKeys, onRemove, onClearAll }: ActiveFilterChipsProps) {
  const active = (Object.keys(filters) as Array<keyof ScheduleFilters>)
    .filter((k) => filters[k] !== '' && !TOOLBAR_OWNED.has(k));
  if (active.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="active-filter-chips">
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Filter className="h-3 w-3" />Filters
      </span>
      {active.map((key) => {
        const isDefault = defaultKeys?.has(key) ?? false;
        return (
          <span
            key={key}
            className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 text-sky-900 pl-2.5 pr-1 py-0.5 text-xs"
            title={isDefault ? 'Applied by default on this page — remove to see every line' : undefined}
          >
            <span className="text-sky-700">{SCHEDULE_FILTER_LABELS[key]}:</span>
            <span className="font-medium">{describeFilterValue(key, filters[key])}</span>
            {isDefault && <span className="text-[10px] uppercase tracking-wide text-sky-600/80 ml-0.5">default</span>}
            <button
              type="button"
              onClick={() => onRemove(key)}
              className="ml-0.5 rounded-full p-0.5 hover:bg-sky-200 text-sky-700"
              aria-label={`Remove filter ${SCHEDULE_FILTER_LABELS[key]}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={onClearAll}
        className="text-xs text-primary hover:underline ml-1"
      >
        Clear all
      </button>
    </div>
  );
}
